import css from './DataTable.module.scss';
import { useEffect, useState } from 'react';
import SVGDisplay, { SVGType } from '../SVGDisplay/SVGDisplay';
import { DataError } from '../../../types/appTypes';
import Spinner from '../Spinner/Spinner';
import { CountList } from '../../../types/appModels';
import { DebounceInput } from 'react-debounce-input';

// types
type ActionHandler<R extends DataTableRow> = (action: string, row: R) => void;

export type FilterHandler = (filters: FilterColumn[]) => void;

type ValueRender<R extends DataTableRow> = (
  row: R,
  handleAction: ActionHandler<R>
) => JSX.Element;

type FilterRender<R extends DataTableRow> = (
  column: DataColumn<R>,
  handleFilter: FilterHandler
) => JSX.Element;

type IsActionable<R extends DataTableRow> = (row: R) => boolean;

export type ActionDefinition<R extends DataTableRow> = {
  action: string;
  label: string;
  allowed?: IsActionable<R>;
};

// enums
export enum SortDirection {
  None,
  Ascending,
  Descending,
}

export enum DisplayAlignment {
  Left,
  Center,
  Right,
}

export enum TableStatus {
  Idle,
  Loading,
  Acting,
  Sorting,
  Filtering,
  Success,
  Fail,
  Error,
}

// sort
export type SortColumn = {
  columnId: string;
  direction: SortDirection;
};

// filter
export type FilterOption = {
  label: string;
  value: string;
};

export type FilterColumn = {
  columnId: string;
  filter?: string;
};

type DataFilterProps<R extends DataTableRow> = {
  column: DataColumn<R>;
  handleFilter: FilterHandler;
  defaultFilter?: string;
};

type DateDataFilterProps<R extends DataTableRow> = {
  column: DataColumn<R>;
  handleFilter: FilterHandler;
  defaultFromFilter?: string | number;
  defaultToFilter?: string | number;
};

export const TextDataFilter = <R extends DataTableRow>({
  column,
  handleFilter,
  defaultFilter,
}: DataFilterProps<R>) => {
  const [filterState, setFilterState] = useState(defaultFilter);

  useEffect(() => {
    if (defaultFilter !== undefined) {
      handleFilter([{ columnId: column.id, filter: defaultFilter }]);
    }
  }, []);

  return (
    <DebounceInput
      debounceTimeout={500}
      value={filterState}
      onChange={(e) => {
        const newVal = e.target.value === '' ? undefined : e.target.value;
        const fc: FilterColumn = {
          columnId: column.id,
          filter: newVal,
        };
        setFilterState(newVal);
        handleFilter([fc]);
      }}
    />
  );
};

export const DateRangeFilter = <R extends DataTableRow>({
  column,
  handleFilter,
  defaultFromFilter,
  defaultToFilter,
}: DateDataFilterProps<R>) => {
  const [filterState, setFilterState] = useState({
    from: defaultFromFilter,
    to: defaultToFilter,
  });

  useEffect(() => {
    let setFrom = false;
    let setTo = false;
    let newFromFilter = defaultFromFilter;
    let newToFilter = defaultToFilter;

    if (newFromFilter !== undefined && typeof newFromFilter === 'number') {
      const filtFromDate = new Date(newFromFilter);
      const year = filtFromDate.getFullYear();
      const month = (filtFromDate.getMonth() + 1).toString().padStart(2, '0');
      const day = filtFromDate.getDate().toString().padStart(2, '0');
      const hours = filtFromDate.getHours().toString().padStart(2, '0');
      const mins = filtFromDate.getMinutes().toString().padStart(2, '0');
      newFromFilter = `${year}-${month}-${day}T${hours}:${mins}`;
      setFrom = true;
    }

    if (newToFilter !== undefined && typeof newToFilter === 'number') {
      const filtToDate = new Date(newToFilter);
      const year = filtToDate.getFullYear();
      const month = (filtToDate.getMonth() + 1).toString().padStart(2, '0');
      const day = filtToDate.getDate().toString().padStart(2, '0');
      const hours = filtToDate.getHours().toString().padStart(2, '0');
      const mins = filtToDate.getMinutes().toString().padStart(2, '0');
      newToFilter = `${year}-${month}-${day}T${hours}:${mins}`;
      setTo = true;
    }

    if (setFrom || setTo) {
      setFilterState((pv) => {
        return { ...pv, from: newFromFilter, to: newToFilter };
      });
    }

    if (newFromFilter !== undefined || newToFilter !== undefined) {
      const filters: FilterColumn[] = [];

      if (newFromFilter !== undefined) {
        filters.push({
          columnId: `${column.id}From`,
          filter: newFromFilter.toString(),
        });
      }

      if (newToFilter !== undefined) {
        filters.push({
          columnId: `${column.id}To`,
          filter: newToFilter.toString(),
        });
      }

      handleFilter(filters);
    }
  }, []);

  return (
    <div className={css.dateRange}>
      <div>
        <input
          type='datetime-local'
          value={filterState.from}
          onChange={(e) => {
            const newVal = e.target.value === '' ? undefined : e.target.value;
            const fc: FilterColumn = {
              columnId: `${column.id}From`,
              filter: newVal,
            };
            setFilterState((pv) => {
              return { ...pv, from: newVal };
            });
            handleFilter([fc]);
          }}
        />
      </div>
      <div>
        <input
          type='datetime-local'
          value={filterState.to}
          onChange={(e) => {
            const newVal = e.target.value === '' ? undefined : e.target.value;
            const fc: FilterColumn = {
              columnId: `${column.id}To`,
              filter: newVal,
            };
            setFilterState((pv) => {
              return { ...pv, to: newVal };
            });
            handleFilter([fc]);
          }}
        />
      </div>
    </div>
  );
};

type OptionsDataFilterProps<R extends DataTableRow> = {
  column: DataColumn<R>;
  options: FilterOption[];
  handleFilter: FilterHandler;
};

export const OptionsDataFilter = <R extends DataTableRow>({
  column,
  options,
  handleFilter,
}: OptionsDataFilterProps<R>) => {
  return (
    <select
      onChange={(e) => {
        const fc: FilterColumn = {
          columnId: column.id,
          filter: e.target.value === '' ? undefined : e.target.value,
        };
        handleFilter([fc]);
      }}
    >
      <option value=''></option>
      {options.map((fo) => (
        <option key={fo.value} value={fo.value}>
          {fo.label}
        </option>
      ))}
    </select>
  );
};

// state
export type TableState<T> = {
  status: TableStatus;
  error?: DataError;
  data?: CountList<T>;
  sort?: SortColumn;
  filters?: FilterColumn[];
};

// row
export interface DataTableRow {
  [key: string]: string | number | boolean | Date | undefined;
  id: string;
}

// columns
export interface DataColumn<R extends DataTableRow> {
  id: string;
  title: string;
  align: DisplayAlignment;
  sortable: boolean;
  filterable: boolean;
  widthPercent?: number;
  renderFilter: FilterRender<R>;
  render: ValueRender<R>;
}

abstract class BaseColumn<R extends DataTableRow> implements DataColumn<R> {
  id: string;
  title: string;
  align: DisplayAlignment;
  sortable: boolean;
  filterable: boolean;
  widthPercent?: number;
  customRenderFilter?: FilterRender<R>;

  constructor(
    id: string,
    title: string,
    align: DisplayAlignment = DisplayAlignment.Left,
    sortable: boolean = true,
    filterable: boolean = false,
    widthPercent?: number,
    customRenderFilter?: FilterRender<R>
  ) {
    this.id = id;
    this.title = title;
    this.align = align;
    this.sortable = sortable;
    this.filterable = filterable;
    this.widthPercent = widthPercent;
    this.customRenderFilter = customRenderFilter;
  }

  abstract render: ValueRender<R>;

  renderFilter: FilterRender<R> = (
    column: DataColumn<R>,
    handleFilter: FilterHandler
  ) => {
    if (!this.filterable) return <>&nbsp;</>;

    if (this.customRenderFilter === undefined)
      return <TextDataFilter column={column} handleFilter={handleFilter} />;

    return this.customRenderFilter(column, handleFilter);
  };
}

export class ActionsColumn<R extends DataTableRow> extends BaseColumn<R> {
  definitions: ActionDefinition<R>[];

  constructor(
    id: string,
    title: string = 'Actions',
    definitions: ActionDefinition<R>[],
    widthPercent?: number
  ) {
    super(id, title, DisplayAlignment.Center, false, false, widthPercent);
    this.definitions = definitions;
  }

  render: ValueRender<R> = (row, handleAction) => {
    const actions: JSX.Element[] = [];

    for (let definition of this.definitions) {
      if (definition.allowed === undefined || definition.allowed(row)) {
        actions.push(
          <span className={css.action}>
            [
            <ClickWrapper
              action={definition.action}
              row={row}
              allowAction={definition.allowed}
              handleAction={handleAction}
            >
              {definition.label}
            </ClickWrapper>
            ]
          </span>
        );
      }
    }

    return <div className={css.actions}>{actions}</div>;
  };
}

export class TextDataColumn<R extends DataTableRow> extends BaseColumn<R> {
  action?: string;
  allowAction?: IsActionable<R>;

  constructor(
    id: string,
    title: string,
    sortable: boolean = true,
    filterable: boolean = false,
    widthPercent?: number,
    action?: string,
    allowAction?: IsActionable<R>,
    customRenderFilter?: FilterRender<R>,
    align: DisplayAlignment = DisplayAlignment.Left
  ) {
    super(
      id,
      title,
      align,
      sortable,
      filterable,
      widthPercent,
      customRenderFilter
    );
    this.action = action;
    this.allowAction = allowAction;
  }

  render: ValueRender<R> = (row, handleAction) => {
    const v = row[this.id];
    if (v === undefined || v === null) {
      return <></>;
    }

    return (
      <ClickWrapper
        action={this.action}
        row={row}
        allowAction={this.allowAction}
        handleAction={handleAction}
      >
        {v!.toString()}
      </ClickWrapper>
    );
  };
}

export class DateDataColumn<R extends DataTableRow> extends BaseColumn<R> {
  formatter?: Intl.DateTimeFormat;
  action?: string;
  allowAction?: IsActionable<R>;

  constructor(
    id: string,
    title: string,
    sortable: boolean = true,
    filterable: boolean = false,
    widthPercent?: number,
    formatter?: Intl.DateTimeFormat,
    action?: string,
    allowAction?: IsActionable<R>,
    customRenderFilter?: FilterRender<R>,
    align: DisplayAlignment = DisplayAlignment.Left
  ) {
    super(
      id,
      title,
      align,
      sortable,
      filterable,
      widthPercent,
      customRenderFilter
    );
    this.formatter = formatter;
    this.action = action;
    this.allowAction = allowAction;
  }

  render: ValueRender<R> = (row, handleAction) => {
    const v = row[this.id];
    if (v === undefined || v === null || !(v instanceof Date)) {
      return <></>;
    }

    if (this.formatter === undefined) {
      return (
        <ClickWrapper
          action={this.action}
          allowAction={this.allowAction}
          row={row}
          handleAction={handleAction}
        >
          {new Intl.DateTimeFormat(navigator.language, {
            dateStyle: 'short',
            timeStyle: 'long',
            timeZone: 'UTC',
          }).format(v)}
        </ClickWrapper>
      );
    }

    return (
      <ClickWrapper
        action={this.action}
        allowAction={this.allowAction}
        row={row}
        handleAction={handleAction}
      >
        {this.formatter.format(v)}
      </ClickWrapper>
    );
  };
}

export class BooleanDataColumn<R extends DataTableRow> extends BaseColumn<R> {
  action?: string;
  allowAction?: IsActionable<R>;

  constructor(
    id: string,
    title: string,
    sortable: boolean = true,
    filterable: boolean = false,
    widthPercent?: number,
    action?: string,
    allowAction?: IsActionable<R>
  ) {
    super(
      id,
      title,
      DisplayAlignment.Center,
      sortable,
      filterable,
      widthPercent,
      (column, handleFilter) => {
        return (
          <OptionsDataFilter
            column={column}
            handleFilter={handleFilter}
            options={[
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' },
            ]}
          />
        );
      }
    );
    this.action = action;
    this.allowAction = allowAction;
  }

  render: ValueRender<R> = (row, handleAction) => {
    const v = row[this.id];
    if (v === undefined || v === null || v === false) {
      return (
        <ClickWrapper
          action={this.action}
          allowAction={this.allowAction}
          row={row}
          handleAction={handleAction}
        >
          <SVGDisplay type={SVGType.checkboxUnchecked} />
        </ClickWrapper>
      );
    } else {
      return (
        <ClickWrapper
          action={this.action}
          allowAction={this.allowAction}
          row={row}
          handleAction={handleAction}
        >
          <SVGDisplay type={SVGType.checkboxChecked} />
        </ClickWrapper>
      );
    }
  };
}

// data table
type DataTableProps<R extends DataTableRow, T> = {
  columns: readonly DataColumn<R>[];
  rows: readonly R[];
  total: number;
  allowFilter: boolean;
  allowNoSort: boolean;
  allowAdd?: boolean;
  addLabel?: string;
  onSort?: (sortColumn?: SortColumn) => void;
  onFilter?: (filterColumns: FilterColumn[]) => void;
  onLoadMore?: () => void;
  onAdd?: () => void;
  onAction?: (action: string, row: R) => void;
  state: TableState<T>;
};

export const DataTable = <R extends DataTableRow, T>({
  columns,
  rows,
  total,
  allowFilter,
  allowNoSort,
  allowAdd,
  addLabel,
  onSort,
  onFilter,
  onLoadMore,
  onAdd,
  onAction,
  state,
}: DataTableProps<R, T>) => {
  const handleSortClick = (column: DataColumn<R>) => {
    if (onSort === undefined || !column.sortable) {
      return;
    }

    let newDirection = SortDirection.Ascending;
    if (state.sort !== undefined && state.sort.columnId === column.id) {
      if (state.sort.direction === SortDirection.Ascending) {
        newDirection = SortDirection.Descending;
      } else if (state.sort.direction === SortDirection.Descending) {
        newDirection = allowNoSort
          ? SortDirection.None
          : SortDirection.Ascending;
      }
    }

    let newSortColumn: SortColumn | undefined = undefined;
    if (newDirection !== SortDirection.None) {
      newSortColumn = { columnId: column.id, direction: newDirection };
    }

    onSort(newSortColumn);
  };

  const handleFilter = (filters: FilterColumn[]) => {
    if (onFilter === undefined) {
      return;
    }

    onFilter(filters);
  };

  const handleActionClick = (action: string, row: R) => {
    if (onAction !== undefined) onAction(action, row);
  };

  const buildTableHead = (): JSX.Element => {
    const headers: JSX.Element[] = [];
    const headerCells: JSX.Element[] = [];
    const filterCells: JSX.Element[] = [];
    const isFilterOn =
      allowFilter &&
      onFilter !== undefined &&
      columns.filter((c) => c.filterable).length > 0;

    columns.forEach((c) => {
      let sd: JSX.Element = <></>;
      let isSorted = false;
      if (state.sort !== undefined && state.sort.columnId === c.id) {
        isSorted = true;
        sd =
          state.sort.direction === SortDirection.Ascending ? (
            <SVGDisplay type={SVGType.upTriangle} />
          ) : (
            <SVGDisplay type={SVGType.downTriangle} />
          );
      }

      let headerClass;
      if (onSort !== undefined && c.sortable) headerClass = css.sortable;
      if (c.widthPercent !== undefined) {
        headerClass = headerClass === undefined ? '' : `${headerClass} `;

        const widthClass = `width${c.widthPercent.toString()}`;
        headerClass = `${headerClass}${css[widthClass]}`;
      }

      let cellClass;
      if (c.align === DisplayAlignment.Center) cellClass = css.center;
      if (c.align === DisplayAlignment.Right) cellClass = css.right;

      headerCells.push(
        <th className={headerClass} onClick={(e) => handleSortClick(c)}>
          <div>
            <label className={cellClass}>{c.title}</label>
            <div
              className={
                isSorted || c.align !== DisplayAlignment.Right
                  ? css.sort
                  : undefined
              }
            >
              {sd}
            </div>
          </div>
        </th>
      );

      if (isFilterOn) {
        const filterDisplay: JSX.Element = (
          <th className={css.filter}>
            <div>{c.renderFilter(c, handleFilter)}</div>
          </th>
        );
        filterCells.push(filterDisplay);
      }
    });
    headers.push(<tr>{headerCells}</tr>);
    headers.push(<tr>{filterCells}</tr>);

    return <thead>{headers}</thead>;
  };

  const buildTableBody = (): JSX.Element => {
    const body: JSX.Element[] = [];

    if (
      state.status === TableStatus.Acting ||
      state.status === TableStatus.Loading ||
      state.status === TableStatus.Sorting ||
      state.status === TableStatus.Filtering
    ) {
      body.push(
        <tr>
          <td colSpan={columns.length} className={css.loading}>
            <div className={css.spinner}>
              <Spinner />
            </div>
          </td>
        </tr>
      );
    } else if (
      state.status === TableStatus.Fail ||
      state.status === TableStatus.Error
    ) {
      body.push(
        <tr>
          <td colSpan={columns.length} className={css.noData}>
            {state.error !== undefined
              ? state.error!.message
              : 'An error occurred and the data could not be loaded.'}
          </td>
        </tr>
      );
    } else {
      rows.forEach((r) => {
        const cells: JSX.Element[] = [];

        columns.forEach((c) => {
          let cellClass;
          if (c.align === DisplayAlignment.Center) cellClass = css.center;
          if (c.align === DisplayAlignment.Right) cellClass = css.right;

          cells.push(
            <td className={cellClass}>{c.render(r, handleActionClick)}</td>
          );
        });

        if (cells.length > 0) {
          body.push(<tr>{cells}</tr>);
        }
      });

      if (body.length === 0) {
        body.push(
          <tr>
            <td colSpan={columns.length} className={css.noData}>
              There is no data to display
            </td>
          </tr>
        );
      }
    }

    return <tbody>{body}</tbody>;
  };

  const buildTableFooter = (): JSX.Element => {
    let footer = <></>;

    const buttons = [];
    if (onLoadMore != undefined && rows.length > 0 && total > rows.length) {
      buttons.push(<button onClick={onLoadMore}>Load More</button>);
    }
    if (allowAdd && onAdd !== undefined) {
      buttons.push(<button onClick={onAdd}>{addLabel ?? 'Add'}</button>);
    }

    let buttonControls = <></>;
    if (buttons.length > 0) {
      buttonControls = <div className={css.buttons}>{buttons}</div>;
    }

    if (total > 0) {
      footer = (
        <div className={css.gridFooter}>
          <p>
            Showing 1 - {rows.length} of {total}
          </p>
          {buttonControls}
        </div>
      );
    } else if (buttons.length > 0) {
      footer = <div className={css.gridFooter}>{buttonControls}</div>;
    }

    return footer;
  };

  return (
    <div className={css.gridContainer}>
      <div className={css.gridScroller}>
        <table className={css.grid}>
          {buildTableHead()}
          {buildTableBody()}
        </table>
      </div>
      {buildTableFooter()}
    </div>
  );
};

// click wrapper
type ClickWrapperProps<R extends DataTableRow> = {
  action?: string;
  row: R;
  allowAction?: IsActionable<R>;
  handleAction: ActionHandler<R>;
  children: React.ReactNode;
};

const ClickWrapper = <R extends DataTableRow>({
  action,
  row,
  allowAction,
  handleAction,
  children,
}: ClickWrapperProps<R>) => {
  let onValueClick: React.MouseEventHandler | undefined = undefined;
  let valueClass;
  if (
    action !== undefined &&
    (allowAction === undefined || allowAction!(row))
  ) {
    onValueClick = (e) => {
      handleAction(action!, row);
    };
    valueClass = css.actionable;
  }

  return (
    <span className={valueClass} onClick={onValueClick}>
      {children}
    </span>
  );
};
